import { useState, useEffect, useRef, useCallback } from 'react';

const PLAYING_KEY = 'music-playing';
const VOLUME_KEY = 'music-volume';
const DEFAULT_VOLUME = 0.2;
const TRACKS = {
  light: '/sounds/beat-light.mp3',
  dark: '/sounds/beat-dark.mp3',
};

// Shared AudioContext — survives HMR and strict mode remounts
let sharedCtx = null;
function getCtx() {
  if (!sharedCtx || sharedCtx.state === 'closed') {
    sharedCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return sharedCtx;
}

// Cache decoded buffers across remounts
const bufferCache = {};
// Track in-flight fetches to prevent duplicate network requests
const fetchPromises = {};

export default function useBackgroundMusic(isDark) {
  const [playing, setPlaying] = useState(false);
  const [volume, setVolumeState] = useState(() => {
    const stored = localStorage.getItem(VOLUME_KEY);
    return stored !== null ? parseFloat(stored) : DEFAULT_VOLUME;
  });

  const sourcesRef = useRef({});
  const gainsRef = useRef({});
  const activeKeyRef = useRef(null);
  const startCtxTimeRef = useRef(null);
  const offsetRef = useRef(0);
  // Tracks whether we still need to auto-resume from last session
  const pendingAutoResumeRef = useRef(localStorage.getItem(PLAYING_KEY) === 'true');
  // Set to true during the gesture that triggers auto-resume, so togglePlay can skip
  const autoResumedThisClickRef = useRef(false);

  const key = isDark ? 'dark' : 'light';

  // If music was playing last session, auto-start on first user interaction anywhere
  useEffect(() => {
    if (!pendingAutoResumeRef.current) return;

    const startOnGesture = () => {
      if (!pendingAutoResumeRef.current) return;
      pendingAutoResumeRef.current = false;
      autoResumedThisClickRef.current = true;
      // Reset flag after this event loop so only this click is affected
      setTimeout(() => { autoResumedThisClickRef.current = false; }, 0);
      const ctx = getCtx();
      ctx.resume();
      setPlaying(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('click', startOnGesture, true);
      window.removeEventListener('touchstart', startOnGesture, true);
      window.removeEventListener('keydown', startOnGesture, true);
    };

    // Use capture phase so this fires BEFORE the button's onClick (togglePlay)
    window.addEventListener('click', startOnGesture, true);
    window.addEventListener('touchstart', startOnGesture, true);
    window.addEventListener('keydown', startOnGesture, true);

    return cleanup;
  }, []);

  const loadBuffer = useCallback(async (trackKey) => {
    if (bufferCache[trackKey]) return bufferCache[trackKey];
    if (fetchPromises[trackKey]) return fetchPromises[trackKey];
    const ctx = getCtx();
    fetchPromises[trackKey] = fetch(TRACKS[trackKey])
      .then((res) => res.arrayBuffer())
      .then((buf) => ctx.decodeAudioData(buf))
      .then((decoded) => {
        bufferCache[trackKey] = decoded;
        delete fetchPromises[trackKey];
        return decoded;
      })
      .catch(() => { delete fetchPromises[trackKey]; });
    return fetchPromises[trackKey];
  }, []);

  // Prefetch only the current theme's track (lazy)
  useEffect(() => {
    if (!bufferCache[key]) loadBuffer(key);
  }, [key, loadBuffer]);

  const getCurrentOffset = useCallback(() => {
    const ctx = getCtx();
    if (startCtxTimeRef.current === null) return offsetRef.current;
    const buffer = bufferCache[activeKeyRef.current];
    if (!buffer) return 0;
    const elapsed = ctx.currentTime - startCtxTimeRef.current;
    return (offsetRef.current + elapsed) % buffer.duration;
  }, []);

  const stopSource = useCallback((trackKey) => {
    if (sourcesRef.current[trackKey]) {
      try { sourcesRef.current[trackKey].stop(); } catch (e) {}
      sourcesRef.current[trackKey] = null;
    }
  }, []);

  const startSource = useCallback((trackKey, offset, gainValue) => {
    const ctx = getCtx();
    const buffer = bufferCache[trackKey];
    if (!buffer) return;

    stopSource(trackKey);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    if (!gainsRef.current[trackKey]) {
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gainsRef.current[trackKey] = gain;
    }
    gainsRef.current[trackKey].gain.setValueAtTime(gainValue, ctx.currentTime);

    source.connect(gainsRef.current[trackKey]);
    source.start(0, offset % buffer.duration);
    sourcesRef.current[trackKey] = source;
  }, [stopSource]);

  // Main effect: handles play/pause AND theme switches
  useEffect(() => {
    const otherKey = key === 'dark' ? 'light' : 'dark';
    activeKeyRef.current = key;

    // Always stop the opposite track's source first to prevent orphaned playback.
    // This guards against rapid theme switches where a previous crossfade timeout
    // was cancelled before it could clean up.
    stopSource(otherKey);
    // Also zero out the other track's gain to kill any scheduled ramps
    if (gainsRef.current[otherKey]) {
      const ctx = getCtx();
      gainsRef.current[otherKey].gain.cancelScheduledValues(ctx.currentTime);
      gainsRef.current[otherKey].gain.setValueAtTime(0, ctx.currentTime);
    }

    if (!playing) {
      offsetRef.current = getCurrentOffset();
      startCtxTimeRef.current = null;
      stopSource(key);
      return;
    }

    let cancelled = false;

    (async () => {
      const ctx = getCtx();
      if (ctx.state === 'suspended') await ctx.resume();

      await loadBuffer(key);
      if (cancelled) return;

      // Re-check after async gap: if the key changed while we were loading,
      // another effect invocation owns playback now — bail out.
      if (activeKeyRef.current !== key) return;

      const currentOffset = getCurrentOffset();

      startSource(key, currentOffset, volume);

      offsetRef.current = currentOffset;
      startCtxTimeRef.current = ctx.currentTime;
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, playing]);

  // Sync volume to active gain node
  useEffect(() => {
    const gain = gainsRef.current[key];
    if (gain) {
      const ctx = getCtx();
      gain.gain.setValueAtTime(volume, ctx.currentTime);
    }
  }, [volume, key]);

  // Cleanup sources on unmount (but never close the shared context)
  useEffect(() => {
    const sources = sourcesRef.current;
    return () => {
      Object.keys(sources).forEach((k) => {
        try { sources[k]?.stop(); } catch (e) {}
      });
    };
  }, []);

  const togglePlay = useCallback(() => {
    // If auto-resume already started playback on this same click (capture phase),
    // skip the toggle so the music stays playing.
    if (autoResumedThisClickRef.current) return;

    const ctx = getCtx();
    if (ctx.state === 'suspended') ctx.resume();

    setPlaying((prev) => {
      const next = !prev;
      localStorage.setItem(PLAYING_KEY, String(next));
      return next;
    });
  }, []);

  const setVolume = useCallback((val) => {
    const clamped = Math.max(0, Math.min(1, val));
    setVolumeState(clamped);
    localStorage.setItem(VOLUME_KEY, String(clamped));
  }, []);

  return { playing, togglePlay, volume, setVolume };
}
